// THE ANGLER on screen (sim/bosses/angler.ts). One rigid GLB (Assets/bosses/angler/angler.glb,
// packed into public/assets/run/ by pack_glb.mjs) posed procedurally by node NAME: pelvis, torso,
// head, jaw, lure_rod, arm_<L|R>_upper/_fore, hand_<L|R>, leg_<L|R>_thigh/_shin, tank, panel_*,
// wp_eye_L/R, wp_lure, wp_belly, fx_palm_<L|R>, sock_hose. A remodel with the same names drops in.
//
// What reads, from across the arena:
//   - the eruption: it rises out of the puddle at the arena centre with a splash and a roar
//   - the flood: a shallow teal pool round the arena centre that spreads with every phase (the
//     arena clock); the pillars and shelves stand dry above it
//   - gold: the small mean eyes always throb; the drum behind the chest porthole blazes when it opens;
//     the lure bulb turns MAGENTA while a gate shell is up (the prime → detonate read)
//   - tells (it stands still for every one): the palm flares cyan (PALM SHOT, a lane to the aim),
//     a leg cocks back (KICK, a fan), a knee lifts (STOMP, a ring), the hose turns on itself
//     (SELF-DOUSE), the nozzle braces down a locked cone (PRESSURE HOSE), arms go up over
//     locked strike rings (GEYSER FIELD / BOMB HURL), the jaw drops in a roar (PIRANHA CALL),
//     arms cross behind a red shell (WATER BARRIER), it crouches over a flood-wide strike disc
//     (MAELSTROM, the super)
//   - wet: the paint goes dark and glossy and drips; frozen: it frosts over
//
// The model (tools/boss_forge/angler.py, 2026-10 piranha remodel): `head` is the WHOLE piranha hull
// (jaw, eyes, lure, tank, dorsal fin ride on it), pivoted at the neck under it; `torso` is the chassis
// the arms hang from. One textured `paint` material (baked atlas) + glow_* emissives.
//   - panels fly off to blasts and the bare plating glows ember red under them

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { BossBeat, BossKind } from '../../../../sim/bosses/types';
import {
  ANGLER_SCALE, APart, ATK_NAMES, AngBit, AngEv, AnglerNet, Ast, Atk, BARRIER_R, BARRIER_Y, FLOOD, FLOOD_DEPTH, PANEL_NODES, PART_ARMOR, PART_NAMES,
  anglerRaycast,
} from '../../../../sim/bosses/angler';
import { EFlag, NetEnemy } from '../../../../sim/protocol';
import { withHaze } from '../../materials';
import type { HudBoss, HudObjective } from '../../contracts';
import { DANGER, Decal, groundDecal } from './decals';
import type { BossAim, BossClient, BossClientFactory, BossCtx, BossFrame } from './types';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const MODEL = BASE + 'assets/run/angler.glb';
const NODES = ['pelvis', 'torso', 'head', 'jaw', 'lure_rod', 'tank', 'arm_L_upper', 'arm_R_upper', 'arm_L_fore', 'arm_R_fore', 'hand_L', 'hand_R',
  'leg_L_thigh', 'leg_R_thigh', 'leg_L_shin', 'leg_R_shin', 'foot_L', 'foot_R', 'sock_hose', 'fx_palm_L', 'fx_palm_R', 'wp_lure', 'wp_belly'] as const;
type NodeName = (typeof NODES)[number];
const FLOOD_MAX = 44;
/** `head` is the whole hull: its nods are scaled down so the fish rears, it doesn't flip */
const HEAD_K = 0.55;
const MAGENTA = new THREE.Color(1.0, 0.25, 0.85);
/** the eyes + lure: dim ember pinpricks, not lamps (the gate still flips the lure to MAGENTA) */
const EMBER = new THREE.Color(1.0, 0.3, 0.05);
const CYAN = new THREE.Color(0.25, 0.95, 1.0);
const WATER_COL = new THREE.Color(0.3, 0.85, 1.0);
const FROST = new THREE.Color(0.78, 0.9, 1.0);

// ------------------------------------------------------------------ shells + jets (additive, one draw each)
const SHELL_VERT = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying vec3 vP;
void main() {
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vN = normalize( mat3( modelMatrix ) * normal ); vV = normalize( cameraPosition - wp.xyz ); vP = position;
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;
const SHELL_FRAG = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying vec3 vP;
uniform vec3 uCol; uniform float uK; uniform float uTime;
void main() {
  float f = pow( 1.0 - abs( dot( vN, vV ) ), 2.2 );
  float hex = step( 0.86, fract( vP.y * 3.0 + uTime * 0.6 ) ) * 0.35 + step( 0.9, fract( atan( vP.x, vP.z ) * 2.5 ) ) * 0.25;
  float a = ( f * 1.1 + hex * f + 0.06 ) * uK;
  gl_FragColor = vec4( uCol * a, a );
}`;
const JET_FRAG = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying vec3 vP;
uniform vec3 uCol; uniform float uK; uniform float uTime;
void main() {
  // vP.y runs 0..1 along the jet (geometry translated): streaks race outwards
  float along = vP.y;
  float streak = 0.55 + 0.45 * sin( atan( vP.x, vP.z ) * 9.0 + along * 4.0 - uTime * 22.0 );
  float core = pow( abs( dot( vN, vV ) ), 1.5 );
  float a = ( 0.25 + 0.75 * core ) * streak * uK * smoothstep( 1.0, 0.75, along ) * smoothstep( 0.0, 0.05, along );
  gl_FragColor = vec4( mix( uCol, vec3( 1.0 ), core * 0.5 ) * a, a );
}`;

function additive(frag: string, col: THREE.Color, time: { value: number }) {
  return new THREE.ShaderMaterial({
    vertexShader: SHELL_VERT, fragmentShader: frag,
    uniforms: { uCol: { value: col.clone() }, uK: { value: 0 }, uTime: time },
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  });
}

interface Fling { mesh: THREE.Mesh; vx: number; vy: number; vz: number; rx: number; rz: number; t: number }
interface Column { x: number; y: number; z: number; t: number }

class AnglerClient implements BossClient {
  readonly kind = BossKind.Angler;
  readonly title = 'THE ANGLER';
  readonly sub = 'THE FIFTH COWBOY';
  private root = new THREE.Group();
  private model: THREE.Object3D | null = null;
  private n: Partial<Record<NodeName, THREE.Object3D>> = {};
  private rest = new Map<THREE.Object3D, { p: THREE.Vector3; r: THREE.Euler }>();
  private panels: (THREE.Object3D | null)[] = [];
  private paint: THREE.MeshStandardMaterial | null = null;
  private paintCol = new THREE.Color();
  private paintRough = 1;
  private eyeMat: THREE.MeshStandardMaterial | null = null;
  private lureMat: THREE.MeshStandardMaterial | null = null;
  private bellyMat: THREE.MeshStandardMaterial | null = null;
  private palmMat: [THREE.MeshStandardMaterial | null, THREE.MeshStandardMaterial | null] = [null, null];
  private ember: THREE.MeshStandardMaterial;
  private scars: THREE.Mesh[] = [];
  private shell: THREE.Mesh; private shellMat: THREE.ShaderMaterial;
  private barrier: THREE.Mesh; private barrierMat: THREE.ShaderMaterial;
  private jet: THREE.Mesh; private jetMat: THREE.ShaderMaterial;
  private columns: THREE.Mesh[] = []; private columnMat: THREE.ShaderMaterial; private colState: Column[] = [];
  private flood: THREE.Mesh | null = null; private floodMat: THREE.MeshStandardMaterial | null = null;
  private floodU = { uR: { value: 0 }, uT: { value: 0 } };
  private floodHome = { x: NaN, z: NaN };
  private floodR = 0;
  private fan: Decal; private lane: Decal; private ring: Decal; private strikes: Decal[] = [];
  private flings: Fling[] = [];
  private disposables: { dispose(): void }[] = [];
  private v1 = new THREE.Vector3(); private v2 = new THREE.Vector3();
  private part = { t: -1, part: APart.None, idx: -1 };
  private open = { belly: false, barrier: false, panels: 0 };
  private last = { x: 0, z: 0, walk: 0, spd: 0, has: false };
  private lastNet: AnglerNet | null = null;
  private lastPts: { x: number; z: number }[] = [];
  private shedSeen = 0;
  private dead: { t: number; x: number; y: number; z: number; yaw: number } | null = null;
  private wetK = 0; private frozenK = 0;
  private dripT = 0;

  constructor(private ctx: BossCtx) {
    this.root.visible = false;
    ctx.scene.add(this.root);
    new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(MODEL)
      .then((g) => { this.onModel(g.scene); ctx.prewarm?.([this.root]); })
      .catch((e) => console.warn('[angler] model failed', e));
    const sphere = new THREE.SphereGeometry(1, 32, 20);
    this.disposables.push(sphere);
    this.shellMat = additive(SHELL_FRAG, MAGENTA, ctx.time);
    this.shell = new THREE.Mesh(sphere, this.shellMat);
    this.barrierMat = additive(SHELL_FRAG, new THREE.Color(1.0, 0.18, 0.1), ctx.time);
    this.barrier = new THREE.Mesh(sphere, this.barrierMat);
    const jg = new THREE.CylinderGeometry(1.1, 0.25, 1, 20, 6, true);
    jg.translate(0, 0.5, 0);
    this.jetMat = additive(JET_FRAG, CYAN, ctx.time);
    this.jet = new THREE.Mesh(jg, this.jetMat);
    const cg = new THREE.CylinderGeometry(1, 1.25, 1, 20, 6, true);
    cg.translate(0, 0.5, 0);
    this.columnMat = additive(JET_FRAG, WATER_COL, ctx.time);
    this.columnMat.uniforms.uK.value = 1;
    for (let i = 0; i < 4; i++) {
      const c = new THREE.Mesh(cg, this.columnMat);
      c.visible = false; c.frustumCulled = false; c.renderOrder = 44;
      this.columns.push(c); ctx.scene.add(c);
    }
    this.disposables.push(this.shellMat, this.barrierMat, jg, this.jetMat, cg, this.columnMat);
    for (const m of [this.shell, this.barrier, this.jet]) { m.visible = false; m.frustumCulled = false; m.renderOrder = 45; ctx.scene.add(m); }
    this.ember = new THREE.MeshStandardMaterial({ color: 0x3a1208, emissive: 0xff4a12, emissiveIntensity: 1.4, roughness: 0.8 });
    this.disposables.push(this.ember);
    this.fan = groundDecal(ctx.scene, ctx.time, 2, DANGER);
    this.lane = groundDecal(ctx.scene, ctx.time, 0, DANGER);
    this.ring = groundDecal(ctx.scene, ctx.time, 1, DANGER);
    for (let i = 0; i < 4; i++) this.strikes.push(groundDecal(ctx.scene, ctx.time, 3, DANGER));
  }

  // ------------------------------------------------------------------ model
  private onModel(scene: THREE.Object3D) {
    this.model = scene;
    scene.scale.setScalar(ANGLER_SCALE);
    this.root.add(scene);
    const mats = new Map<THREE.Material, THREE.Material>();
    const own = (m: THREE.Material, key: string) => {
      const k = mats.get(m);
      if (k && key === 'shared') return k;
      const c = withHaze((m as THREE.MeshStandardMaterial).clone(), m.name === 'paint' ? 0.35 : 0.15);
      this.disposables.push(c);
      if (key === 'shared') mats.set(m, c);
      return c;
    };
    scene.traverse((o) => {
      if (o !== scene) this.rest.set(o, { p: o.position.clone(), r: o.rotation.clone() });
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true; mesh.receiveShadow = true; mesh.frustumCulled = false;
      this.disposables.push(mesh.geometry);
      const src = mesh.material as THREE.MeshStandardMaterial;
      let mat: THREE.Material;
      if (src.name === 'paint') {
        mat = own(src, 'shared');
        this.paint = mat as THREE.MeshStandardMaterial;
        this.paintCol.copy(this.paint.color);
        this.paintRough = this.paint.roughness;
      } else {
        // each glowing part gets its own material so it can throb on its own
        const host = mesh.parent?.name ?? '';
        const nm = mesh.name || host;
        const tag = /eye/.test(nm) || /eye/.test(host) ? 'eye' : /lure/.test(nm) || /lure/.test(host) ? 'lure' : /belly/.test(nm) || /belly/.test(host) ? 'belly'
          : /palm_L/.test(nm) || /palm_L/.test(host) ? 'palmL' : /palm_R/.test(nm) || /palm_R/.test(host) ? 'palmR' : 'other';
        const ex = tag === 'eye' ? this.eyeMat : tag === 'lure' ? this.lureMat : tag === 'belly' ? this.bellyMat : tag === 'palmL' ? this.palmMat[0] : tag === 'palmR' ? this.palmMat[1] : null;
        mat = ex ?? own(src, tag);
        const sm = mat as THREE.MeshStandardMaterial;
        if (tag === 'eye') { this.eyeMat = sm; sm.emissive.copy(EMBER); sm.color.copy(EMBER); } else if (tag === 'lure') this.lureMat = sm; else if (tag === 'belly') this.bellyMat = sm;
        else if (tag === 'palmL') this.palmMat[0] = sm; else if (tag === 'palmR') this.palmMat[1] = sm;
      }
      mesh.material = mat;
    });
    for (const k of NODES) this.n[k] = scene.getObjectByName(k) ?? undefined;
    // glTF multi-primitive meshes load as a Group named after the node holding child meshes
    this.panels = PANEL_NODES.map((nm) => scene.getObjectByName(nm) ?? null);
    // ember plating under each panel (shown once it's shed): a box the size of the panel's bounds
    const box = new THREE.Box3();
    scene.updateMatrixWorld(true);
    const inv = new THREE.Matrix4();
    for (const p of this.panels) {
      if (!p) { this.scars.push(new THREE.Mesh()); continue; }
      box.makeEmpty();
      inv.copy(p.parent!.matrixWorld).invert();
      p.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.geometry.computeBoundingBox();
        const b = m.geometry.boundingBox!.clone().applyMatrix4(m.matrixWorld).applyMatrix4(inv);
        box.union(b);
      });
      const sz = box.getSize(this.v1), c = box.getCenter(this.v2);
      const g = new THREE.BoxGeometry(Math.max(0.05, sz.x * 0.8), Math.max(0.05, sz.y * 0.8), Math.max(0.05, sz.z * 0.8));
      this.disposables.push(g);
      const scar = new THREE.Mesh(g, this.ember);
      scar.position.copy(c);
      scar.visible = false;
      p.parent!.add(scar);
      this.scars.push(scar);
    }
  }

  private reset() {
    for (const [o, r] of this.rest) { o.position.copy(r.p); o.rotation.copy(r.r); }
  }

  // ------------------------------------------------------------------ the frame
  update(f: BossFrame | null, time: number, dt: number) {
    this.fan.mesh.visible = this.lane.mesh.visible = this.ring.mesh.visible = false;
    for (const s of this.strikes) s.mesh.visible = false;
    this.jet.visible = false;
    this.stepColumns(dt);
    this.stepFlings(dt);
    const e = f?.primary ?? null;
    const net = f ? (f.net as AnglerNet) : null;
    if (net && f) this.drawFlood(net.homeX, net.homeZ, net.flood, f, time);
    if (!e || !net || !f) {
      this.shell.visible = this.barrier.visible = false;
      this.dying(time, dt);
      return;
    }
    this.dead = null;
    this.lastNet = net;
    if (net.pts.length) this.lastPts = net.pts.map((p) => ({ x: p.x, z: p.z }));
    this.root.visible = !!this.model;
    this.pose(e, net, f, time, dt);
    this.glows(e, net, f, time, dt);
    this.telegraphs(e, net, f, time);
  }

  private pose(e: NetEnemy, net: AnglerNet, f: BossFrame, time: number, dt: number) {
    const L = this.last;
    if (!L.has) { L.x = e.x; L.z = e.z; L.has = true; }
    const spd = Math.hypot(e.x - L.x, e.z - L.z) / Math.max(1e-3, dt);
    L.spd += (Math.min(9, spd) - L.spd) * Math.min(1, dt * 8);
    L.x = e.x; L.z = e.z;
    L.walk += dt * Math.min(9, L.spd * 1.25);
    const sw = Math.min(1, L.spd / 2.5);
    let y = e.y;
    // the eruption: it rises out of the puddle
    if (e.st === 5) { const k = Math.min(1, e.prog); y -= 8.5 * (1 - k) * (1 - k); }
    this.root.position.set(e.x, y, e.z);
    this.root.rotation.set(0, e.yaw + Math.PI, 0);
    if (!this.model) return;
    this.reset();
    const N = this.n;
    const r = (k: NodeName) => N[k]?.rotation;
    const k = e.prog, st = e.st, atk = net.atk;
    const tell = st === 1, exec = st === 2, rec = st === 3 || st === 4;
    const gate = (net.phase & AngBit.Gate) !== 0;
    // ---- the walk (always underneath)
    const s1 = Math.sin(L.walk), c1 = Math.cos(L.walk);
    if (r('leg_L_thigh')) r('leg_L_thigh')!.x = s1 * 0.55 * sw;
    if (r('leg_R_thigh')) r('leg_R_thigh')!.x = -s1 * 0.55 * sw;
    if (r('leg_L_shin')) r('leg_L_shin')!.x = Math.max(0, -c1) * 0.7 * sw;
    if (r('leg_R_shin')) r('leg_R_shin')!.x = Math.max(0, c1) * 0.7 * sw;
    if (r('arm_L_upper')) r('arm_L_upper')!.x = -s1 * 0.35 * sw;
    if (r('arm_R_upper')) r('arm_R_upper')!.x = s1 * 0.35 * sw;
    if (N.pelvis) N.pelvis.position.y -= Math.abs(c1) * 0.08 * sw;
    if (r('torso')) { r('torso')!.y = s1 * 0.06 * sw; r('torso')!.x = 0.08 * sw; }
    // idle breathing: the gunslinger's sway
    if (r('head')) r('head')!.x = HEAD_K * (Math.sin(time * 1.3) * 0.04);
    if (r('lure_rod')) { r('lure_rod')!.x = Math.sin(time * 2.1) * 0.12; r('lure_rod')!.z = Math.sin(time * 1.4) * 0.08; }
    if (r('jaw')) r('jaw')!.x = 0.05 + Math.max(0, Math.sin(time * 0.9)) * 0.05;
    // ---- attacks
    const ease = (t: number) => t * t * (3 - 2 * t);
    if (tell || exec) {
      switch (atk) {
        case Atk.Palm: {
          // the right arm comes up level, the palm flares (Iron-Man)
          const a = tell ? ease(k) : 1;
          if (r('arm_R_upper')) r('arm_R_upper')!.x = -1.45 * a;
          if (r('arm_R_fore')) r('arm_R_fore')!.x = -0.15 * a;
          if (r('hand_R')) r('hand_R')!.x = -1.2 * a;
          if (exec && r('arm_R_upper')) r('arm_R_upper')!.x -= Math.max(0, Math.sin(k * Math.PI * 4)) * 0.18;
          if (r('torso')) r('torso')!.y = -0.25 * a;
          break;
        }
        case Atk.Kick: {
          const a = tell ? ease(k) : 1;
          if (r('leg_R_thigh')) r('leg_R_thigh')!.x = tell ? 0.75 * a : 0.75 - 2.2 * ease(Math.min(1, k * 2));
          if (r('leg_R_shin')) r('leg_R_shin')!.x = tell ? 1.1 * a : 1.1 * (1 - Math.min(1, k * 2));
          if (r('torso')) r('torso')!.x = tell ? -0.15 * a : 0.2;
          if (r('arm_L_upper')) r('arm_L_upper')!.z = 0.5 * a;
          if (r('arm_R_upper')) r('arm_R_upper')!.z = -0.5 * a;
          break;
        }
        case Atk.Stomp: {
          const a = tell ? ease(k) : 1 - ease(Math.min(1, k * 3));
          if (r('leg_L_thigh')) r('leg_L_thigh')!.x = -1.3 * a;
          if (r('leg_L_shin')) r('leg_L_shin')!.x = 1.4 * a;
          if (r('torso')) r('torso')!.x = -0.12 * a;
          if (r('arm_L_upper')) r('arm_L_upper')!.z = 0.7 * a;
          if (r('arm_R_upper')) r('arm_R_upper')!.z = -0.7 * a;
          if (N.pelvis) N.pelvis.position.y += 0.25 * a;
          break;
        }
        case Atk.Douse: case Atk.Summon: {
          // head back, jaw wide: the roar (douse: the hose turns on itself)
          const a = tell ? ease(k) : 1;
          if (r('head')) r('head')!.x = HEAD_K * (-0.45 * a);
          if (r('jaw')) r('jaw')!.x = 0.65 * a + (exec ? Math.sin(time * 30) * 0.05 : 0);
          if (r('arm_L_upper')) { r('arm_L_upper')!.z = 1.1 * a; r('arm_L_upper')!.x = -0.6 * a; }
          if (r('arm_R_upper')) { r('arm_R_upper')!.z = -1.1 * a; r('arm_R_upper')!.x = -0.6 * a; }
          if (r('torso')) r('torso')!.x = -0.18 * a;
          break;
        }
        case Atk.Hose: {
          // braced, nozzle levelled down the locked line
          const a = tell ? ease(k) : 1;
          if (r('torso')) { r('torso')!.x = 0.15 * a; r('torso')!.y = 0.35 * a; }
          if (r('arm_R_upper')) r('arm_R_upper')!.x = -1.1 * a;
          if (r('arm_L_upper')) r('arm_L_upper')!.x = -0.9 * a;
          if (r('leg_L_thigh')) r('leg_L_thigh')!.x = -0.4 * a;
          if (r('leg_R_thigh')) r('leg_R_thigh')!.x = 0.35 * a;
          if (N.pelvis) N.pelvis.position.y -= 0.25 * a;
          if (exec) this.ctx.shake(0.02);
          break;
        }
        case Atk.Geyser: case Atk.Bomb: case Atk.Maelstrom: {
          // arms high (the geyser call / the wind-up / the leap), then down
          const a = tell ? ease(k) : atk === Atk.Maelstrom ? 1 : 1 - ease(Math.min(1, k * 2.5));
          const both = atk !== Atk.Bomb;
          if (r('arm_L_upper')) r('arm_L_upper')!.x = -2.7 * a;
          if (both && r('arm_R_upper')) r('arm_R_upper')!.x = -2.7 * a;
          if (r('arm_L_fore')) r('arm_L_fore')!.x = -0.4 * a;
          if (r('head')) r('head')!.x = HEAD_K * (-0.25 * a);
          if (atk === Atk.Maelstrom && tell && N.pelvis) N.pelvis.position.y -= 0.55 * a;
          if (atk === Atk.Maelstrom && tell) { if (r('leg_L_thigh')) r('leg_L_thigh')!.x = -0.6 * a; if (r('leg_R_thigh')) r('leg_R_thigh')!.x = -0.6 * a; if (r('leg_L_shin')) r('leg_L_shin')!.x = 1.0 * a; if (r('leg_R_shin')) r('leg_R_shin')!.x = 1.0 * a; }
          break;
        }
        case Atk.Barrier: {
          const a = tell ? ease(k) : 1;
          if (r('arm_L_upper')) { r('arm_L_upper')!.x = -1.3 * a; r('arm_L_upper')!.y = 0.6 * a; }
          if (r('arm_R_upper')) { r('arm_R_upper')!.x = -1.3 * a; r('arm_R_upper')!.y = -0.6 * a; }
          if (r('arm_L_fore')) r('arm_L_fore')!.x = -1.2 * a;
          if (r('arm_R_fore')) r('arm_R_fore')!.x = -1.2 * a;
          if (r('head')) r('head')!.x = HEAD_K * (0.3 * a);
          if (N.pelvis) N.pelvis.position.y -= 0.3 * a;
          break;
        }
      }
    } else if (rec || gate) {
      // spent: slumped forward, arms hanging, the drum on show
      const a = gate ? 0.7 : st === 4 ? 1 : 1 - ease(Math.max(0, (k - 0.75) * 4));
      if (r('torso')) r('torso')!.x = 0.3 * a;
      if (r('head')) r('head')!.x = HEAD_K * (0.25 * a);
      if (r('jaw')) r('jaw')!.x = 0.3 * a;
      if (r('arm_L_upper')) { r('arm_L_upper')!.x = 0.15 * a; r('arm_L_upper')!.z = 0.12 * a; }
      if (r('arm_R_upper')) { r('arm_R_upper')!.x = 0.15 * a; r('arm_R_upper')!.z = -0.12 * a; }
      if (N.pelvis) N.pelvis.position.y -= 0.3 * a;
      if (r('leg_L_thigh')) r('leg_L_thigh')!.x = -0.35 * a;
      if (r('leg_R_thigh')) r('leg_R_thigh')!.x = -0.35 * a;
      if (r('leg_L_shin')) r('leg_L_shin')!.x = 0.6 * a;
      if (r('leg_R_shin')) r('leg_R_shin')!.x = 0.6 * a;
      if (st === 4 && r('torso')) r('torso')!.z = Math.sin(time * 23) * 0.03;
    }
    // ---- panels: blown off ones are gone, the plating under them smoulders
    for (let i = 0; i < this.panels.length; i++) {
      const p = this.panels[i], off = (net.panels >> i) & 1;
      if (p) p.visible = !off;
      if (this.scars[i]) this.scars[i].visible = !!off;
      if (off && !((this.shedSeen >> i) & 1)) { this.shedSeen |= 1 << i; this.flingPanel(i); }
    }
    this.ember.emissiveIntensity = 1.1 + 0.4 * Math.sin(time * 5);
    this.root.updateMatrixWorld(true);
  }

  private glows(e: NetEnemy, net: AnglerNet, f: BossFrame, time: number, dt: number) {
    const gate = (net.phase & AngBit.Gate) !== 0;
    const wet = (net.phase & AngBit.Wet) !== 0, frozen = (net.phase & AngBit.Frozen) !== 0;
    const belly = (net.phase & AngBit.Belly) !== 0;
    this.wetK += ((wet ? 1 : 0) - this.wetK) * Math.min(1, dt * 3);
    this.frozenK += ((frozen ? 1 : 0) - this.frozenK) * Math.min(1, dt * 6);
    // G7 gold: the eyes always throb
    if (this.eyeMat) this.eyeMat.emissiveIntensity = 1.1 + 0.6 * (0.5 + 0.5 * Math.sin(time * 6.5));
    if (this.lureMat) {
      this.lureMat.emissive.copy(gate ? MAGENTA : EMBER);
      this.lureMat.color.copy(gate ? MAGENTA : EMBER);
      this.lureMat.emissiveIntensity = gate ? 2.6 + 1.6 * Math.sin(time * 9) : 0.75 + 0.25 * Math.sin(time * 4);
    }
    if (this.bellyMat) this.bellyMat.emissiveIntensity = belly ? 3 + 1.5 * Math.sin(time * 14) : 0.12;
    const palm = net.atk === Atk.Palm && (e.st === 1 || e.st === 2) ? (e.st === 1 ? e.prog : 1) : 0;
    if (this.palmMat[1]) this.palmMat[1].emissiveIntensity = 1 + palm * 5 + (e.st === 2 && net.atk === Atk.Palm ? 2 * Math.max(0, Math.sin(time * 40)) : 0);
    if (this.palmMat[0]) this.palmMat[0].emissiveIntensity = net.atk === Atk.Bomb && e.st === 1 ? 1 + 4 * e.prog : 1;
    // the enamel: wet = dark + glossy, frozen = frosted, primed = magenta flicker, hit = flash
    if (this.paint) {
      const p = this.paint;
      p.color.copy(this.paintCol).multiplyScalar(1 - 0.28 * this.wetK);
      p.color.lerp(FROST, this.frozenK * 0.6);
      p.roughness = this.paintRough * (1 - 0.65 * this.wetK - 0.25 * this.frozenK);
      const h = f.hitAge(e.id);
      const fl = h < 0.1 ? 1 - h / 0.1 : 0;
      const primed = (e.flags & EFlag.Primed) !== 0 ? 0.5 + 0.5 * Math.sin(time * 10) : 0;
      p.emissive.setRGB(0.5 * fl + 0.55 * primed + 0.12 * this.frozenK, 0.45 * fl + 0.1 * primed + 0.2 * this.frozenK, 0.4 * fl + 0.5 * primed + 0.3 * this.frozenK);
      p.emissiveIntensity = 1;
    }
    // drips off a wet fish
    const fx = this.ctx.fx();
    if (fx && this.wetK > 0.5) {
      this.dripT -= dt;
      if (this.dripT <= 0) {
        this.dripT = 0.18;
        const a = Math.random() * Math.PI * 2;
        fx.element(this.v1.set(e.x + Math.cos(a) * 1.0, e.y + 3 + Math.random() * 5, e.z + Math.sin(a) * 1.0), 4, 0.35);
      }
    }
    // the shells
    const c = this.v1.set(e.x, this.root.position.y + BARRIER_Y, e.z);
    this.shell.visible = gate;
    if (gate) { this.shell.position.copy(c); this.shell.scale.setScalar(4.8 + 0.08 * Math.sin(time * 3)); this.shellMat.uniforms.uK.value = 0.9; }
    const bar = (net.phase & AngBit.Barrier) !== 0 || (net.atk === Atk.Barrier && e.st === 1);
    this.barrier.visible = bar;
    if (bar) {
      const k = net.atk === Atk.Barrier && e.st === 1 ? e.prog : 1;
      this.barrier.position.copy(c); this.barrier.scale.setScalar(BARRIER_R * (0.6 + 0.4 * k) + 0.05 * Math.sin(time * 7));
      this.barrierMat.uniforms.uK.value = 0.4 + 0.8 * k;
    }
  }

  private telegraphs(e: NetEnemy, net: AnglerNet, f: BossFrame, time: number) {
    const tell = e.st === 1, exec = e.st === 2;
    if (!tell && !exec) return;
    const gy = f.ground(e.x, e.z);
    const pts = net.pts.length ? net.pts : this.lastPts;
    const k = tell ? e.prog : 1;
    switch (net.atk) {
      case Atk.Palm: {
        // a lane from the palm to the locked aim point
        const p = pts[0];
        if (!p || !tell) break;
        const dx = p.x - e.x, dz = p.z - e.z, d = Math.hypot(dx, dz);
        this.lane.mesh.visible = true;
        this.lane.mesh.position.set(e.x + dx * 0.5, gy + 0.1, e.z + dz * 0.5);
        this.lane.mesh.rotation.set(0, Math.atan2(-dx, -dz), 0);
        this.lane.mesh.scale.set(1.4, 1, d);
        this.lane.u.uK.value = k;
        break;
      }
      case Atk.Kick: {
        this.fan.mesh.visible = true;
        this.fan.mesh.position.set(e.x, gy + 0.09, e.z);
        this.fan.mesh.rotation.set(0, e.yaw, 0);
        this.fan.mesh.scale.set(12.4, 1, 12.4);
        this.fan.u.uArc.value = 0.7; this.fan.u.uK.value = k;
        break;
      }
      case Atk.Hose: {
        const p = pts[0];
        if (!p) break;
        const dx = p.x - e.x, dz = p.z - e.z;
        const yaw = Math.atan2(-dx, -dz);
        if (tell) {
          this.fan.mesh.visible = true;
          this.fan.mesh.position.set(e.x, gy + 0.09, e.z);
          this.fan.mesh.rotation.set(0, yaw, 0);
          this.fan.mesh.scale.set(26, 1, 26);
          this.fan.u.uArc.value = 0.3; this.fan.u.uK.value = k;
        } else {
          // the jet: from the nozzle down the locked line
          const noz = this.n.sock_hose;
          if (noz) noz.getWorldPosition(this.v1); else this.v1.set(e.x, e.y + 4.5, e.z);
          const ty = f.ground(p.x, p.z) + 1.2;
          this.v2.set(e.x + (dx / (Math.hypot(dx, dz) || 1)) * 13, ty, e.z + (dz / (Math.hypot(dx, dz) || 1)) * 13).sub(this.v1);
          const len = this.v2.length();
          this.jet.visible = true;
          this.jet.position.copy(this.v1);
          this.jet.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.v2.normalize());
          this.jet.scale.set(1.6, len, 1.6);
          this.jetMat.uniforms.uK.value = 1;
          const fx = this.ctx.fx();
          if (fx && Math.random() < 0.5) fx.element(this.v1.copy(this.jet.position).addScaledVector(this.v2, len * (0.5 + Math.random() * 0.5)), 4, 0.6);
        }
        break;
      }
      case Atk.Stomp: {
        this.ring.mesh.visible = true;
        this.ring.mesh.position.set(e.x, gy + 0.1, e.z);
        this.ring.mesh.scale.set(16, 1, 16);
        this.ring.u.uR.value = 0.94 + 0.04 * Math.sin(time * 16);
        break;
      }
      case Atk.Geyser: case Atk.Bomb: {
        const r = net.atk === Atk.Geyser ? 2.4 : 3.6;
        for (let i = 0; i < pts.length && i < this.strikes.length; i++) {
          const d = this.strikes[i], p = pts[i];
          d.mesh.visible = true;
          d.mesh.position.set(p.x, f.ground(p.x, p.z) + 0.12, p.z);
          d.mesh.scale.set(r * 2.1, 1, r * 2.1);
          d.u.uK.value = tell ? k * 0.8 : 0.8 + 0.2 * k;
        }
        break;
      }
      case Atk.Maelstrom: {
        // the whole flood is the danger (the perches are the answer)
        const d = this.strikes[0];
        d.mesh.visible = true;
        d.mesh.position.set(net.homeX, f.ground(net.homeX, net.homeZ) + FLOOD_DEPTH + 0.06, net.homeZ);
        d.mesh.scale.set(net.flood * 2, 1, net.flood * 2);
        d.u.uK.value = k;
        break;
      }
    }
  }

  // ------------------------------------------------------------------ the flood
  private drawFlood(hx: number, hz: number, r: number, f: BossFrame | { ground(x: number, z: number): number }, time: number) {
    if (!this.flood || hx !== this.floodHome.x || hz !== this.floodHome.z) this.buildFlood(hx, hz, f.ground);
    this.floodR += (r - this.floodR) * 0.2;
    this.floodU.uR.value = this.floodR;
    this.floodU.uT.value = time;
    this.flood!.visible = this.floodR > 0.5;
  }

  private buildFlood(hx: number, hz: number, ground: (x: number, z: number) => number) {
    if (this.flood) { this.ctx.scene.remove(this.flood); this.flood.geometry.dispose(); }
    this.floodHome.x = hx; this.floodHome.z = hz;
    const RINGS = 40, SEG = 72;
    const pos: number[] = [], idx: number[] = [];
    const base = ground(hx, hz);
    pos.push(0, ground(hx, hz) - base + FLOOD_DEPTH, 0);
    for (let i = 1; i <= RINGS; i++) {
      const rr = FLOOD_MAX * Math.pow(i / RINGS, 1.15);
      for (let j = 0; j < SEG; j++) {
        const a = (j / SEG) * Math.PI * 2, x = Math.cos(a) * rr, z = Math.sin(a) * rr;
        const g = ground(hx + x, hz + z);
        pos.push(x, (g < -1e8 ? -50 : g - base) + FLOOD_DEPTH, z);
      }
    }
    for (let j = 0; j < SEG; j++) idx.push(0, 1 + ((j + 1) % SEG), 1 + j);
    for (let i = 1; i < RINGS; i++) {
      const a0 = 1 + (i - 1) * SEG, a1 = 1 + i * SEG;
      for (let j = 0; j < SEG; j++) {
        const j1 = (j + 1) % SEG;
        idx.push(a0 + j, a0 + j1, a1 + j, a0 + j1, a1 + j1, a1 + j);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    // flatten the normals: it's water, it lies level
    const nrm = geo.getAttribute('normal') as THREE.BufferAttribute;
    for (let i = 0; i < nrm.count; i++) nrm.setXYZ(i, 0, 1, 0);
    if (!this.floodMat) {
      const m = new THREE.MeshStandardMaterial({ color: 0x2d8a8c, roughness: 0.08, metalness: 0.1, transparent: true, opacity: 0.8, depthWrite: false });
      const U = this.floodU;
      m.onBeforeCompile = (sh) => {
        sh.uniforms.uR = U.uR; sh.uniforms.uT = U.uT;
        sh.vertexShader = sh.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec2 vFl;')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFl = position.xz;');
        sh.fragmentShader = sh.fragmentShader
          .replace('#include <common>', '#include <common>\nvarying vec2 vFl; uniform float uR; uniform float uT;')
          .replace('#include <color_fragment>', `#include <color_fragment>
            float fr = length( vFl );
            if ( fr > uR ) discard;
            float edge = smoothstep( uR - 1.6, uR - 0.1, fr );
            float rip = 0.5 + 0.5 * sin( fr * 2.3 - uT * 2.6 + sin( atan( vFl.y, vFl.x ) * 5.0 ) * 0.6 );
            diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.55, 0.9, 0.88 ), rip * 0.18 );
            diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.95, 0.93, 0.82 ), edge * 0.85 );
            diffuseColor.a *= mix( 0.82, 1.0, edge );`);
      };
      this.floodMat = withHaze(m, 0.8);
      this.disposables.push(this.floodMat);
    }
    this.flood = new THREE.Mesh(geo, this.floodMat);
    this.flood.position.set(hx, base, hz);
    this.flood.renderOrder = 5; this.flood.frustumCulled = false; this.flood.receiveShadow = true;
    this.flood.visible = false;
    this.ctx.scene.add(this.flood);
  }

  // ------------------------------------------------------------------ effects
  private flingPanel(i: number) {
    const p = this.panels[i];
    if (!p) return;
    p.updateMatrixWorld(true);
    p.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const c = new THREE.Mesh(m.geometry, m.material);
      m.matrixWorld.decompose(c.position, c.quaternion, c.scale);
      c.castShadow = true;
      this.ctx.scene.add(c);
      const a = this.root.rotation.y + (i % 2 ? -1 : 1) * 1.2;
      this.flings.push({ mesh: c, vx: Math.sin(a) * 6, vy: 9, vz: Math.cos(a) * 6, rx: 6 + i, rz: 4 - i, t: 0 });
    });
    p.getWorldPosition(this.v1);
    this.ctx.fx()?.explosion(this.v1, 1.6, 1);
    this.ctx.sfx.play('debris_clatter', this.v1);
  }

  private stepFlings(dt: number) {
    for (let i = this.flings.length - 1; i >= 0; i--) {
      const f = this.flings[i];
      f.t += dt; f.vy -= 22 * dt;
      f.mesh.position.x += f.vx * dt; f.mesh.position.y += f.vy * dt; f.mesh.position.z += f.vz * dt;
      f.mesh.rotation.x += f.rx * dt; f.mesh.rotation.z += f.rz * dt;
      if (f.t > 1.8) { this.ctx.scene.remove(f.mesh); this.flings.splice(i, 1); }
    }
  }

  private stepColumns(dt: number) {
    for (let i = 0; i < this.columns.length; i++) {
      const c = this.columns[i], s = this.colState[i];
      if (!s || s.t >= 1.1) { c.visible = false; continue; }
      s.t += dt;
      const up = Math.min(1, s.t / 0.18), down = Math.max(0, (s.t - 0.6) / 0.5);
      c.visible = true;
      c.position.set(s.x, s.y, s.z);
      c.scale.set(2.4 * (1 - down * 0.5), 13 * up * (1 - down * 0.3), 2.4 * (1 - down * 0.5));
    }
    this.columnMat.uniforms.uK.value = 1;
  }

  private dying(time: number, dt: number) {
    const d = this.dead;
    if (!d) { this.root.visible = false; if (this.flood && !this.lastNet) this.flood.visible = false; this.drainFlood(dt); return; }
    d.t += dt;
    // the collapse: it tips back into its own flood and sinks
    const k = Math.min(1, d.t / 1.4), sink = Math.max(0, d.t - 1.2);
    this.root.position.set(d.x, d.y - sink * 2.2, d.z);
    this.root.rotation.set(-1.35 * k * k, d.yaw, 0.15 * k);
    this.root.visible = !!this.model && d.t < 5;
    // the final surge fills the arena, then drains away
    this.floodU.uR.value = this.floodR = Math.min(FLOOD[4], this.floodR + dt * 9);
    this.floodU.uT.value = time;
    if (d.t > 5) { this.dead = null; this.lastNet = null; }
  }

  private drainFlood(dt: number) {
    if (!this.flood || this.floodR <= 0) return;
    this.floodR = Math.max(0, this.floodR - dt * 6);
    this.floodU.uR.value = this.floodR;
    this.flood.visible = this.floodR > 0.5;
  }

  // ------------------------------------------------------------------ the contract
  bodyDied(_type: number, x: number, y: number, z: number) {
    this.dead = { t: 0, x, y, z, yaw: this.root.rotation.y };
    this.root.visible = !!this.model;
    const fx = this.ctx.fx();
    fx?.explosion(this.v1.set(x, y + 3, z), 6, 1);
    for (let i = 0; i < 4; i++) fx?.element(this.v1.set(x + (Math.random() - 0.5) * 4, y + 1, z + (Math.random() - 0.5) * 4), 4, 2);
    this.ctx.sfx.play('explosion_l', { x, y: y + 3, z });
    this.ctx.sfx.play('robot_die', { x, y: y + 3, z }, { pitch: 0.5 });
    this.shedSeen = 0;
  }

  onEvent(kind: number, a: number, b: number, _eid: number, f: BossFrame | null) {
    const sfx = this.ctx.sfx, fx = this.ctx.fx();
    const e = f?.primary;
    const pos = e ? { x: e.x, y: e.y + 4, z: e.z } : undefined;
    void b;
    switch (kind) {
      case BossBeat.Intro:
        if (a === 1 && pos) {
          sfx.play('explosion', pos, { pitch: 0.5 }); sfx.play('brute_roar', pos, { pitch: 0.55 }); sfx.play('el_wet', pos);
          for (let i = 0; i < 8; i++) fx?.element(this.v1.set(pos.x + Math.cos(i) * 2.5, pos.y - 3.5, pos.z + Math.sin(i) * 2.5), 4, 2);
          fx?.dust(this.v1.set(pos.x, pos.y - 4, pos.z), 1.2);
          this.ctx.shake(0.8); this.ctx.rumble(1, 0.7, 450);
        }
        break;
      case BossBeat.Attack:
        switch (a) {
          case Atk.Palm: sfx.play('blaster_charge', pos, { pitch: 0.7 }); break;
          case Atk.Kick: case Atk.Stomp: sfx.play('brute_tell', pos, { pitch: 0.7 }); break;
          case Atk.Summon: sfx.play('brute_roar', pos, { pitch: 0.7 }); break;
          case Atk.Maelstrom: sfx.play('boss_sting', pos); sfx.play('brute_roar', pos, { pitch: 0.5 }); this.ctx.rumble(0.6, 0.4, 600); break;
          case Atk.Barrier: sfx.play('shield_wait', pos, { pitch: 0.6 }); break;
          default: sfx.play('metal_tell', pos, { pitch: 0.8 });
        }
        break;
      case AngEv.Slam:
        sfx.play(a ? 'explosion_l' : 'ground_pound', pos, { pitch: 0.75 });
        if (e) { fx?.dust(this.v1.set(e.x, e.y, e.z), 1.3); for (let i = 0; i < 6; i++) fx?.element(this.v1.set(e.x + Math.cos(i) * 3, e.y + 0.3, e.z + Math.sin(i) * 3), 4, 1.5); }
        this.ctx.shake(a ? 0.9 : 0.5); this.ctx.rumble(0.8, 0.6, 300);
        break;
      case AngEv.Geyser:
        sfx.play('explosion', pos, { pitch: 1.4 }); sfx.play('el_wet', pos);
        for (let i = 0; i < this.lastPts.length && i < 4; i++) {
          const p = this.lastPts[i], gy = f ? f.ground(p.x, p.z) : 0;
          this.colState[i] = { x: p.x, y: gy, z: p.z, t: 0 };
          fx?.element(this.v1.set(p.x, gy + 0.5, p.z), 4, 2);
        }
        this.ctx.shake(0.35);
        break;
      case AngEv.Douse:
        sfx.play('el_wet', pos); sfx.play('cool_hiss', pos);
        if (e) for (let i = 0; i < 10; i++) fx?.element(this.v1.set(e.x + (Math.random() - 0.5) * 3, e.y + 6, e.z + (Math.random() - 0.5) * 3), 4, 1.5);
        break;
      case AngEv.Shock:
        sfx.play('el_shock', pos); sfx.play('el_arc', pos);
        if (e) {
          fx?.element(this.v1.set(e.x, e.y + 3.5, e.z), 2, 2);
          for (let i = 0; i < 4; i++) fx?.chainArc(this.v1.set(e.x, e.y + 5, e.z), this.v2.set(e.x + Math.cos(i * 1.6) * 3, e.y + 0.4, e.z + Math.sin(i * 1.6) * 3));
        }
        this.ctx.flash(0.25);
        break;
      case AngEv.Backfire: sfx.play('el_shock', undefined, { pitch: 1.3 }); this.ctx.chroma(0.6); break;
      case AngEv.Freeze: sfx.play('el_freeze', pos); if (e) fx?.element(this.v1.set(e.x, e.y + 3, e.z), 3, 2); break;
      case AngEv.Hiss: sfx.play('cool_hiss', pos, { volume: 0.7 }); if (e) fx?.element(this.v1.set(e.x, e.y + 3, e.z), 5, 1); break; // packSteam moved into fx/water.ts (player-only)
      case AngEv.Gate: sfx.play('boss_phase', pos); sfx.play('el_wet', pos); this.ctx.shake(0.4); break;
      case AngEv.GateBreak: sfx.play('shield_break', pos); this.ctx.flash(0.6); this.ctx.shake(0.6); break;
      case AngEv.Phase: sfx.play('boss_phase2', undefined); break;
      case AngEv.Barrier: sfx.play('shield_wait', pos, { pitch: 0.5 }); break;
      case AngEv.BarrierBreak: sfx.play('shield_break', pos, { pitch: 0.8 }); sfx.play('melee_crit', pos); this.ctx.shake(0.4); break;
      case AngEv.Summon: sfx.play('swarmer_alert', pos, { pitch: 0.8 }); break;
      case AngEv.Panel: break; // drawn when the panel bit flips (update), so late joiners see it too
      case BossBeat.Dead:
        sfx.play('boss_sting', pos); this.ctx.flash(0.6); this.ctx.shake(0.8);
        break;
    }
  }

  ray(f: BossFrame, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ap: number, out: BossAim) {
    out.t = -1; out.kind = 0; out.head = false; out.label = '';
    const e = f.primary, net = f.net as AnglerNet;
    if (!e || e.st === 5) return out;
    this.open.belly = (net.phase & AngBit.Belly) !== 0;
    this.open.barrier = (net.phase & AngBit.Barrier) !== 0;
    this.open.panels = net.panels;
    anglerRaycast(e.x, e.y, e.z, e.yaw, this.open, ox, oy, oz, dx, dy, dz, maxT, this.part);
    if (this.part.t < 0) return out;
    const pa = PART_ARMOR[this.part.part];
    const hyper = (e.flags & EFlag.Hyper) !== 0;
    out.t = this.part.t;
    out.kind = this.part.part === APart.Barrier ? 4 : pa.weak ? 3 : hyper || pa.av > ap ? 4 : 2;
    out.head = pa.weak;
    out.label = this.part.part === APart.Lure && hyper ? 'LURE · CRACK THE SHELL' : PART_NAMES[this.part.part];
    return out;
  }

  hud(f: BossFrame, aimLabel: string, o: HudBoss) {
    const net = f.net as AnglerNet, e = f.primary;
    if (!e || e.st === 5) return null;
    o.name = this.title; o.sub = this.sub;
    let shed = 0;
    for (let i = 0; i < 7; i++) if ((net.panels >> i) & 1) shed++;
    o.count = shed ? `${shed} OF 7 PANELS OFF` : '';
    o.hp = e.hp;
    if (o.pips.length !== 3) o.pips = ['EYES', 'DRUM', 'LURE'].map((label) => ({ label, frac: 1, broken: false, open: false }));
    const gate = (net.phase & AngBit.Gate) !== 0;
    o.pips[0].open = true;
    o.pips[1].open = (net.phase & AngBit.Belly) !== 0;
    o.pips[2].open = gate;
    o.hyper = gate; o.meter = 'TIDE SHELL'; o.crack = net.crack;
    const wet = (net.phase & AngBit.Wet) !== 0;
    o.note = e.st === 1 && net.atk ? ATK_NAMES[net.atk]
      : (net.phase & AngBit.Frozen) ? 'FROZEN: BURST IT' : (net.phase & AngBit.Belly) ? 'DRUM OPEN' : wet ? 'WET: SHOCK IT' : '';
    o.warn = net.atk === Atk.Maelstrom && e.st === 1 ? 'GET OUT OF THE WATER' : (net.phase & AngBit.Barrier) ? 'MELEE THE BARRIER' : '';
    o.aimPart = aimLabel;
    return o;
  }

  objective(f: BossFrame | null, o: HudObjective) {
    const net = f ? (f.net as AnglerNet) : null, e = f?.primary;
    o.title = 'LAND THE ANGLER';
    if (!net || !e) { o.detail = 'THOSE LITTLE EMBER EYES ARE SOFT: SHOOT THEM'; return; }
    const ph = net.phase;
    if (net.atk === Atk.Maelstrom && e.st === 1) { o.title = 'MAELSTROM'; o.detail = 'GET OUT OF THE WATER: PERCH OR FLY'; o.tone = 2; return; }
    if (ph & AngBit.Barrier) { o.title = 'WATER BARRIER'; o.detail = 'RUN IN AND HIT IT (MELEE)'; o.tone = 2; return; }
    if (ph & AngBit.Gate) { o.detail = 'TIDE SHELL: PRIME IT, THEN DETONATE (OR CRACK IT THROUGH THE LURE)'; return; }
    if (ph & AngBit.Frozen) { o.detail = 'FROZEN SOLID: OPEN FIRE'; o.tone = 1; return; }
    if (ph & AngBit.Belly) { o.detail = 'THE DRUM IS OPEN: SHOOT THE GOLD PORTHOLE'; o.tone = 1; return; }
    if (ph & AngBit.Wet) { o.detail = 'IT IS WET: SHOCK IT FROM DRY GROUND'; return; }
    o.detail = (ph & AngBit.PhaseMask) === 1 ? 'THE EYES ARE ALWAYS SOFT; THE DRUM OPENS AFTER A KICK OR A STOMP' : 'BLASTS KNOCK ITS PANELS OFF; THE EYES ARE ALWAYS SOFT';
  }

  dispose() {
    this.ctx.scene.remove(this.root, this.shell, this.barrier, this.jet, ...this.columns);
    if (this.flood) { this.ctx.scene.remove(this.flood); this.flood.geometry.dispose(); }
    for (const f of this.flings) this.ctx.scene.remove(f.mesh);
    this.fan.dispose(); this.lane.dispose(); this.ring.dispose();
    for (const s of this.strikes) s.dispose();
    for (const d of this.disposables) d.dispose();
  }
}

export const anglerClient: BossClientFactory = (ctx) => new AnglerClient(ctx);
